let gql = null;
import('graphql-'+'tag').then(module => gql = module.gql, () => {});


/**
 * **import GraphQLBuilder from '@src/hylo-components/utils/graphqlbuilder/graphqlbuilder';**
 *
 * For the functions "createRequest" and "createCall" which return a gql formatted request,
 * at least 'npm install graphql-tag --save-dev' is required
 * which is already included in 'npm install graphql --save-dev'.
 *
 * Class for stitching together a GraphQL query.
 *
 * You can define the field structure of models, add nested models to a model
 * and build queries out of the defined models.
 * You don't need to actually write a query but build a query by structuring
 * your defined models into a tree. At any point of your tree structure you can add
 * additional fields and models while you're building your query.
 *
 * You only need to define your models once and can reuse them whenever and wherever you need to
 * build a query.
 *
 * Build your models in one file, at one place and use them everywhere in your app to create your requests.
 * "createRequest" and "createCall" returned requests are already wrapped in *gql\`...\`* 
 * and ready to use with an ApolloClient.
 *
 * **All methods are static.**
 *
 *
 * Example:
 *
 * ```
 * GraphQLBuilder.addModel('festival', ["uuid", "title", "subtitle"]);
 * GraphQLBuilder.addModel('occurrence', ["uuid", "title", "subtitle"]);
 * GraphQLBuilder.addModel('allEventReferences', ["uuid", "eventType"], {
 *   occurrence: GraphQLBuilder.getModel('occurrence(limit: 10)', ["description", "startDate"])
 * });
 *
 * let query = GraphQLBuilder.createQuery('allEventReferences(limit: $limit, offset: 0)',
 *   GraphQLBuilder.getModel('allEventReferences', {
 *     festival: GraphQLBuilder.getModel('festival')
 *   })
 * );
 * let request = GraphQLBuilder.createRequest('query AllEventReferences($limit: Int)', query);
 * ```
 *
 * https://graphiql-online.com is a good tool for testing.
 */
export default class GraphQLBuilder {

  /** @internal */
  constructor() {}


  /**
   * Adds a fields array, GraphQLBuilderModel or a query to the general GraphQLBuilder library.
   * The general library is separated from the GraphQLBuilder models library.
   *
   * Use it to store field arrays, queries and models including their arguments, variables, directive, alias.
   * Also possible is to store a request as string.
   *
   * @param name                        the name under which the object gets stored
   * @param fields_or_models_or_query   the object to be stored
   */
  public static add(name: string, fields_or_models_or_query: string|string[]|GraphQLBuilderModel) {

    GraphQLBuilder.general[name.trim()] = fields_or_models_or_query;
  }

  /**
   * Returns a fields array, query or a GraphQLBuilderModel stored in the general library.
   *
   * @param name    the name of the fields array, query or GraphQLBuilderModel as defined with add()
   * @returns string|string[]|GraphQLBuilderModel
   */
  public static get(name: string): any {

    if (!GraphQLBuilder.general[name.trim()]) throw new Error('Error: Fields array or model "'+name+'" not included in general GraphQLBuilder library.');

    return GraphQLBuilder.general[name.trim()];
  }

  /**
   * Creates a GraphQLBuilderModel and adds it to the GraphQLBuilder models library.
   *
   * You can add additional fields and models when you are building a query or extend the model with getModel().
   * Define simple building blocks with which you can create more complex queries and models later on.
   *
   * @param name    the name of the model
   * @param fields  an array of field names ("primitive" types, not nested models)
   * @param models  a dictionary of nested models, the "keys" are the field names of the models
   */
  public static createModel(name: string, fields?: string[], models?: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>) {

    if (!fields) fields = [];
    if (!models) models = {};

    const model: GraphQLBuilderModel = {
                                          fields: [...fields],
                                          models: {...models},
                                          arguments: '',
                                          directive: '',
                                          alias: '',
                                          tunnel: false,
                                          tunnel_fields: [],
                                          tunnel_models: {}
                                        };
    GraphQLBuilder.models[name.trim()] = model;
  }

  /**
   * Adds a GraphQLBuilderModel to the GraphQLBuilder models library.
   *
   * More precise, it creates and adds a copy of the provided model.
   * Clears possible attached arguments, variables, directive and/or alias.
   *
   * If you want to store a model including arguments, variables, directive and/or alias,
   * retrieve the model with getModel() and store it in the general library for later use:
   *
   * ```
   * GraphQLBuilder.add('myExtendedModel', GraphQLBuilder.getModel('someModel(limit: $limit)', ['additionalField']));
   * const query = GraphQLBuilder.createQuery('someQuery', GraphQLBuilder.getModel(rootModel, {
   *   extendedModel: GraphQLBuilder.get('myExtendedModel')
   * }));
   * ```
   *
   * @param name    the name of the model
   * @param model   the model to add
   */
  public static addModel(name: string, model: GraphQLBuilderModel) {

    const copy:GraphQLBuilderModel = {
                                      fields: [...model.fields],
                                      models: {...model.models},
                                      arguments: '',
                                      directive: '',
                                      alias: '',
                                      tunnel: model.tunnel,
                                      tunnel_fields: [...model.tunnel_fields],
                                      tunnel_models: {...model.tunnel_models}
                                     };

    GraphQLBuilder.models[name.trim()] = copy;
  }

  /**
   * Returns a GraphQLBuilderModel from the GraphQLBuilder models library.
   *
   * You can add additional fields and/or models to the returned model.
   * The returned model is not the original model stored in the GraphQLBuilder models library
   * but a copy extended by the additionally provided fields and models.
   *
   * With the name you can provide arguments, variables, a directive and/or an alias:
   * - name
   * - name(limit: 10)
   * - name @include(if: $onlyTrue)
   * - alias: name(limit: $limit)
   * The name is parsed for these additional information and is not passed/used as provided.
   *
   * You can create a tunnel for chained models where the additionally provided fields and models
   * are not added to the model of the function call but tunnel through the chain to the last child model
   * of the tunnel.
   * To create a tunnel set the tunnel flag for each member of the chain.
   * A tunnel can not be used directly but needs to be saved first to the GraphQLBuilder models library with addModel().
   * A tunnel needs to be created in one call. You can not use saved tunnels to create another tunnel:
   *
   * ```
   * GraphQLBuilder.addModel('tunnel', GraphQLBuilder.getModel('firstLevelModel', {
   *   child: GraphQLBuilder.getModel('secondLevelModel', {}, {}, true)
   * }, {}, true));
   *
   * GraphQLBuilder.getModel('tunnel', ['field'], {model: GraphQLBuilder.getModel('someModel')});
   * ```
   *
   * The additional "field" and "model" are passed to the "child" secondLevelModel and not added
   * to the firstLevelModel.
   * You can deactivate the tunnel by setting the "tunnel" flag to false when calling/getting the tunnel.
   *
   * @param name              the name of the model as defined with addModel() including arguments, variables, directive, alias
   * @param fields_or_models  additional fields or models
   * @param models            additional models when also additional fields where provided
   * @param tunnel            pass provided additional fields and models down to the last child model in a row which is also set "tunnel"
   */
  public static getModel(name: string, fields_or_models?: string[]|DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>, models?: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>, tunnel?: boolean): GraphQLBuilderModel {

    let args: string = '';
    const args_start = name.indexOf('(');
    if (args_start > 0) {
      args = name.substr(args_start).trim();
      name = name.substr(0, args_start);
    }
    name = name.trim();

    let directive: string = '';
    const directive_start = name.indexOf('@');
    if (directive_start > 0) {
      directive = name.substr(directive_start).trim();
      name = name.substr(0, directive_start).trim();
    }

    let alias: string = '';
    const alias_start = name.indexOf(':');
    if (alias_start > 0) {
      alias = name.substr(0, alias_start+1).trim();
      name = name.substr(alias_start+1).trim();
    }


    if (!GraphQLBuilder.models[name]) throw new Error('Error: Model "'+name+'" not included in GraphQLBuilder models library.');

    const fields: string[] = (Array.isArray(fields_or_models)) ? fields_or_models : [];
    models = (fields_or_models && !Array.isArray(fields_or_models)) ? fields_or_models : models;
    if (!models) models = {};


    let temp_fields: string[], temp_models: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>;
    let temp_tunnel_fields: string[], temp_tunnel_models: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>;
    if (tunnel !== false && tunnel !== true) tunnel = !(!(GraphQLBuilder.models[name].tunnel));

    if (tunnel) {
      temp_fields = [...GraphQLBuilder.models[name].fields, ...GraphQLBuilder.models[name].tunnel_fields];
      temp_models = {...GraphQLBuilder.models[name].models, ...GraphQLBuilder.models[name].tunnel_models};
      temp_tunnel_fields = [...fields];
      temp_tunnel_models = {...models};
    } else {
      temp_fields = [...GraphQLBuilder.models[name].fields, ...GraphQLBuilder.models[name].tunnel_fields, ...fields];
      temp_models = {...GraphQLBuilder.models[name].models, ...GraphQLBuilder.models[name].tunnel_models, ...models};
      temp_tunnel_fields = [];
      temp_tunnel_models = {};
    }

    const model:GraphQLBuilderModel = {
                                        fields: temp_fields,
                                        models: temp_models,
                                        arguments: args,
                                        directive: directive,
                                        alias: alias,
                                        tunnel: tunnel,
                                        tunnel_fields: temp_tunnel_fields,
                                        tunnel_models: temp_tunnel_models
                                       };

    return model;
  }

  /**
   * Creates a query, mutation or fragment.
   *
   * If it is a query or mutation, define the header with the root query field name while also including arguments, variables and/or aliases:
   * - someQuery(id: 100)
   * - someAlias: someQuery(id: $id)
   * ... or any other setup you like.
   *
   * If it is a fragment you are creating, the header should look somehow like this:
   * - fragment comparisonFields on SomeType
   * You can reference the fragemnt by providing "*...comparisonFields*" as field to a model.
   *
   * The header is placed as provided right before your root model definition.
   *
   * Since you are providing a header, possibly defined arguments, directive and alias of the given root model are ignored.
   *
   * @param header  the header of your query
   * @param model   the root model of your query (optional)
   */
  public static createQuery(header: string, model?: GraphQLBuilderModel): string {

    if (!model) return header;

    const root_model: GraphQLBuilderModel = {
      fields: model.fields,
      models: model.models,
      arguments: '',
      directive: '',
      alias: '',
      tunnel: model.tunnel,
      tunnel_fields: model.tunnel_fields,
      tunnel_models: model.tunnel_models
    };

    let query: string = header;
    query += GraphQLBuilder.createModelQuery(root_model, null, null);

    return query;
  }

  /**
   * Creates a request, already "gql"-formatted and ready to be sent to the GraphQL server.
   *
   * In the header you can set your request name, define variables and the type of request you are creating:
   * - query AllEventReferences($offset: Int = 0)
   * - mutation CreateReviewForEpisode($ep: Episode!, $review: ReviewInput!)
   *
   * The header is placed as provided in your request.
   *
   * @param header    the header of your request
   * @param queries   one or more queries sent with your request
   * @param fragments one or more fragments used in your queries
   * @returns a GraphQL document to be used with a ApolloClient
   */
  public static createRequest(header: string, queries: string|string[], fragments?: string|string[]): any {

    const request = GraphQLBuilder.stringRequest(header, queries, fragments);

    if (!gql) {
      console.warn('WARNING at "createRequest": In order to receive a gql formatted request, the node module "graphql-tag" has to be installed. Returning the request as string instead.');
      return request;
    } else return gql(request);
  }

  /**
   * Same as createRequest() but returns a string.
   * Needs to be formatted with "gql" in order to be used with the ApolloClient:
   *
   * ```
   * gql(string_request)
   * ```
   *
   * @param header    the header of your request
   * @param queries   one or more queries sent with your request
   * @param fragments one or more fragments used in your queries
   */
  public static stringRequest(header: string, queries: string|string[], fragments?: string|string[]): string {

    let request: string = header+" {\n";
    let i: number, l: number;

    if (Array.isArray(queries)) {
      for (i=0, l=queries.length; i<l; i++) {
        request += queries[i]+"\n";
      }
    } else {
      request += queries+"\n";
    }

    request += "}\n";

    if (Array.isArray(fragments)) {
      for (i=0, l=fragments.length; i<l; i++) {
        request += fragments[i]+"\n";
      }
    } else if (fragments) {
      request += fragments+"\n";
    }

    return request;
  }

  /**
   * Creates a "gql"-formatted request for the hylo-apollo-client component "call" function.
   * The "call" function optimizes the requests done by the ApolloClient but has limitations.
   * Only queries (requests of type "query") which do not use variables nor fragments can be converted into call requests.
   *
   * The id of your call should always be unique to that specific call since it is part of the optimization process.
   *
   * @param id        the request name
   * @param queries   one or more queries sent with your request
   * @returns a GraphQL document to be used with a hylo-apollo-client call
   */
  public static createCall(id: string, queries: string|string[]): any {

    if (id.indexOf('(') >= 0 || id.indexOf(')') >= 0) throw new Error(`Error: "(" or ")" are not permitted in a call identifier but detected in the identifier "${id}".`);

    id = id.trim();

    if (id == '') throw new Error(`Error: To create a call you need to define an identifier.`);

    const header = `query ${id}`;
    const request = GraphQLBuilder.stringRequest(header, queries);

    if (!gql) {
      console.warn('WARNING at "createCall": In order to receive a gql formatted request, the node module "graphql-tag" has to be installed. Returning the request as string instead.');
      return request;
    } else return gql(request);
  }





  private static general = {};
  private static models: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel> = {};


  private static createModelQuery(model: GraphQLBuilderModel, tunnel_fields: string[], tunnel_models: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>): string {

    let fields = model.fields;
    let models = model.models;
    let i: number, l: number, key: string;

    if (model.tunnel) {

      let first: boolean = (tunnel_fields === null && tunnel_models === null);

      // first in tunnel (models tunnel variables are getting tunneled)
      if (first) {
        tunnel_fields = model.tunnel_fields;
        tunnel_models = model.tunnel_models;
      }


      let last = true;
      for (key in models) {
        if (models[key].tunnel) {
          last = false;
          break;
        }
      }

      for (key in model.tunnel_models) {
        if (model.tunnel_models[key].tunnel) {
          last = false;
          break;
        }
      }

      // last in tunnel (models tunnel variables belong to model + tunneled variables)
      if (last) {
        fields = [...fields, ...model.tunnel_fields, ...tunnel_fields];
        models = {...models, ...model.tunnel_models, ...tunnel_models};
      }


      // middle of tunnel (models tunnel variables belong to model)
      if (!first && !last) {
        fields = [...fields, ...model.tunnel_fields];
        models = {...models, ...model.tunnel_models};
      }
    }



    let query: string = " "+model.directive+model.arguments+" {\n";

    for (i=0, l=fields.length; i<l; i++) {
      query += fields[i]+"\n";
    }

    for (key in models) {
      if (models[key].tunnel) {
        query += models[key].alias+" "+key+GraphQLBuilder.createModelQuery(models[key], tunnel_fields, tunnel_models)+"\n";
      } else {
        query += models[key].alias+" "+key+GraphQLBuilder.createModelQuery(models[key], null, null)+"\n";
      }
    }

    query += "}";

    return query;
  }
}




/**
 * Used to build a nested model structure where each model consists of primitive fields and further models.
 *
 * @typeParam fields **string[]** an array of field names ("primitive" types, not nested models)
 * @typeParam models **DictionaryOfGraphQLBuilderModel&lt;GraphQLBuilderModel>** a dictionary of nested models, the "keys" are the field names of the models
 */
interface GraphQLBuilderModel {
  fields: string[];
  models: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>;
  arguments?: string; // for internal use
  directive?: string; // for internal use
  alias?: string; // for internal use
  tunnel?: boolean; // for internal use
  tunnel_fields?: string[]; // for internal use
  tunnel_models?: DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel>; // for internal use
}

/**
 * A dictionary of nested models. The "keys" are the field names of the GraphQLBuilderModel(s).
 */
interface DictionaryOfGraphQLBuilderModel<GraphQLBuilderModel> {
  [key: string]: GraphQLBuilderModel;
}
